import React, { useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { setSolution } from "../../store/actions/solution";
import { setLoadingRoutesList } from "../../store/actions/loading";
import { setCurrentDriver, toggleRoute } from "../../store/actions/map";
import { handleRouteLock } from "../../store/actions/custom";
import { withSnackbar } from "notistack";
import API from "../../utils/axios";
import { Box, Grid, makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { isNotEmptyRoute, isSearchingValueMatch } from "../../utils/helpers";
import { client } from "../map/DriverMarkers";
import RoutesToolbar from "./RoutesToolbar";
import UnscheduledBar from "./UnscheduledBar";
import RouteDetails from "./RouteDetails";
import {
  setShowingRouteId,
  toggleRouteExpandedStatus,
  toggleRouteSelectedStatus
} from "../../store/actions/project";
import {
  scrollToStop,
  setProjectTabValue,
  setRoutesSearchMode
} from "../../store/actions/common";
import SearchBoxMessage from "../common/SearchBoxMessage";
import useListKeyboardCommands from "../project/useListKeyboardCommands";

const useStyles = makeStyles(theme => ({
  listWrapper: ({ noBottomActions }) => ({
    backgroundColor: "#fff",
    paddingBottom: noBottomActions ? 0 : 140,
    "& + &": {
      borderTop: theme.variables.border
    }
  }),
  routesListBody: {
    boxSizing: "border-box"
  },
  fontWeightMedium: {
    fontWeight: 500
  },
  title: {
    color: "#4D4D50",
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    margin: theme.spacing(1.5, 3)
  },
  searchBoxMessage: {
    paddingTop: theme.spacing(3)
  }
  // stopPlaceholderTop: {
  //   height: theme.spacing(2),
  //   marginBottom: theme.spacing(0.5),
  //   width: "80%",
  //   backgroundColor: "rgba(0,0,0,.15)"
  // },
  // stopPlaceholderBottom: {
  //   height: theme.spacing(3),
  //   width: "85%",
  //   marginBottom: theme.spacing(1.75),
  //   backgroundColor: "rgba(0,0,0,.15)"
  // },
  // stopPlaceholderRect: {
  //   width: theme.spacing(4),
  //   height: theme.spacing(4),
  //   backgroundColor: "rgba(0,0,0,.15)",
  //   flexShrink: 0,
  //   marginBottom: theme.spacing(2),
  //   borderRadius: "4px"
  // }
  // stopSPlaceholderBottom: {
  //   height: theme.spacing(3),
  //   width: "85%",
  //   marginBottom: theme.spacing(1.75),
  //   backgroundColor: "rgba(0,0,0,.15)"
  // },
}));

function RouteGroups({
  projectId,
  solution,
  selectedRoutes,
  handleRouteLock,
  setSolution,
  setLoadingRoutesList,
  warnReopen,
  handleReoptimizeSelectedRoutes,
  toggleRouteSelectedStatus,
  toggleRoute,
  isPastProject,
  published,
  shareRoute,
  setRoutesSearchMode,
  isRoutesSearchMode,
  setProjectTabValue,
  setShowingRouteId
  // projectLoadingInitial
}) {
  const classes = useStyles({
    noBottomActions:
      (selectedRoutes && selectedRoutes.length > 0) || isRoutesSearchMode
  });
  const [searchInputValue, setSearchInputValue] = useState("");
  const [haveHovered, setHaveHovered] = useState(false);
  const listKeyboardCommands = useListKeyboardCommands();

  const unassigned = solution ? solution.unassigned.stops : [];
  // Share route
  // Can resend
  const canResend = useCallback(sentAt => {
    const now = Date.now();
    const sentDate = new Date(sentAt).getTime();

    return now > sentDate + 60000;
  }, []);

  const resendToDriver = useCallback(
    (e, id, sentAt) => {
      e.stopPropagation();

      if (canResend(sentAt)) {
        API.post(`/v1/projects/${projectId}/routes/${id}/send`)
          .then(() => {
            const newSolution = { ...solution };
            const routeIndex = newSolution.routes.findIndex(el => el.id === id);

            newSolution.routes[routeIndex].message_sent_at = new Date();
            setSolution(newSolution);
          })
          .catch(error => console.log(error));
      }
    },
    [canResend, solution, setSolution, projectId]
  );

  // Route groups helpers

  // const hasRouteSelected = shift => {
  //   return (
  //     solution.showAll ||
  //     solution.routeGroups[shift].some(backhaulGroup =>
  //       backhaulGroup.some(isRouteSelected)
  //     )
  //   );
  // };

  // const notEmptyShift = shift => {
  //   return (
  //     solution.showAll ||
  //     solution.routeGroups[shift].some(routes =>
  //       routes.every(route => isNotEmptyRoute(route))
  //     )
  //   );
  // };

  // const getVehiclesQuantity = groupId =>
  //   solution.routeGroups[groupId].filter(
  //     group => group[0] && isRouteSelected(group[0])
  //   ).length;

  // Remove driver from route
  const removeDriver = useCallback(
    id => {
      setLoadingRoutesList(true);
      const newSolution = { ...solution };

      const routeIndex = solution.routes.findIndex(el => el.id === id);
      newSolution.routes[routeIndex].driver = null;

      setSolution(newSolution);

      API.put(`/v1/projects/${projectId}/routes/${id}/drivers`, {
        driver_id: null
      })
        .then(() => {
          client &&
            client.send(
              JSON.stringify({
                type: "driver_change",
                route_id: newSolution.routes[routeIndex].id,
                driver_id: null
              })
            );
        })
        .catch(error => console.log(error))
        .finally(() => {
          setLoadingRoutesList(false);
        });
    },
    [projectId, setLoadingRoutesList, solution, setSolution]
  );

  // Show driver marker
  // const [driverIndex, setDriverIndex] = useState(0);
  // const showDriver = useCallback(
  //   backhaulGroup => e => {
  //     e.stopPropagation();

  //     if (backhaulGroup[driverIndex]) {
  //       const routeId = backhaulGroup[driverIndex].id;
  //       const length = backhaulGroup.length;

  //       setCurrentDriver(routeId);
  //       setTimeout(() => setCurrentDriver(null), 1000);

  //       setDriverIndex((driverIndex + 1) % length);
  //     }
  //   },
  //   [driverIndex, setCurrentDriver]
  // );

  useEffect(() => {
    return () => {
      setRoutesSearchMode(false);
    };
  }, []);

  const routeGroupsToShow = useMemo(
    () =>
      Object.keys(solution?.routeGroups).length > 0
        ? solution?.routeGroups[Object.keys(solution?.routeGroups)[0]].filter(
            backhaulGroup => {
              const isRoutesNotEmpty = !backhaulGroup.some(route =>
                isNotEmptyRoute(route)
              );
              if (isRoutesNotEmpty) {
                return false;
              }

              const driverName = `
                ${backhaulGroup[0]?.driver?.first_name}${backhaulGroup[0]?.driver?.last_name}`;
              const vehicleName = backhaulGroup[0].vehicle_name;

              const isSearchingDriverNameMatch = isSearchingValueMatch(
                driverName,
                searchInputValue
              );
              const isSearchingVehicleNameMatch = isSearchingValueMatch(
                vehicleName,
                searchInputValue
              );

              const isSearchingRouteMatch =
                isSearchingDriverNameMatch || isSearchingVehicleNameMatch;
              const isValidSearchingValue =
                searchInputValue !== "" && backhaulGroup[0];

              return isValidSearchingValue ? isSearchingRouteMatch : true;
            }
          )
        : null,
    [searchInputValue, solution.routeGroups]
  );

  useEffect(() => {
    listKeyboardCommands.methods.reset();
    listKeyboardCommands.methods.setListSize(routeGroupsToShow.length);
  }, [routeGroupsToShow, listKeyboardCommands.methods]);

  useEffect(() => {
    if (searchInputValue === "") {
      listKeyboardCommands.methods.stop();
    } else {
      listKeyboardCommands.methods.start();
    }
  }, [listKeyboardCommands.methods, searchInputValue]);

  useEffect(() => {
    const element = document.getElementById(
      `route_details_${listKeyboardCommands.currentIndex}`
    );
    if (element) {
      element.scrollIntoView(false);
    }
  }, [listKeyboardCommands.currentIndex]);

  listKeyboardCommands.methods.setEnterHandler((event, index) => {
    setShowingRouteId(routeGroupsToShow[index][0].id);
  });

  if (
    routeGroupsToShow === null ||
    (!isRoutesSearchMode && Object.keys(solution?.routeGroups).length === 0)
  ) {
    setProjectTabValue(0);
    return <></>;
  }

  return (
    <>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ maxHeight: "100%", overflow: "hidden" }}
      >
        <Grid item>
          <RoutesToolbar
            searchInputValue={searchInputValue}
            setSearchInputValue={value => {
              if (value.length > 0) {
                setRoutesSearchMode(true);
              } else {
                setRoutesSearchMode(false);
              }
              setSearchInputValue(value);
            }}
            handleReoptimizeSelectedRoutes={handleReoptimizeSelectedRoutes}
            shareRoute={shareRoute}
            onSearchFocus={() => {
              listKeyboardCommands.methods.enable();
            }}
            onSearchBlur={() => {
              listKeyboardCommands.methods.disable();
            }}
          />
        </Grid>
        <Grid item style={{ overflow: "auto" }}>
          {routeGroupsToShow.length > 0 && (
            <>
              <Typography className={classes.title}>Your routes</Typography>
              <UnscheduledBar unassigned={unassigned} warnReopen={warnReopen} />
            </>
          )}
          <div id="route_groups_wrapper">
            <div className={classes.listWrapper}>
              <Box
                // position="relative"
                className={classes.routesListBody}
              >
                {routeGroupsToShow.length === 0 && searchInputValue !== "" && (
                  <SearchBoxMessage
                    message={`No routes found for "${searchInputValue}"`}
                    className={classes.searchBoxMessage}
                  />
                )}
                {routeGroupsToShow.length > 0 &&
                  routeGroupsToShow.map((backhaulGroup, backhaulIndex) => {
                    const routeId = backhaulGroup[0].id;
                    return (
                      <RouteDetails
                        index={backhaulIndex}
                        routeId={routeId}
                        backhaulGroup={backhaulGroup}
                        isPastProject={isPastProject}
                        published={published}
                        handleRouteLock={handleRouteLock}
                        toggleRouteSelectedStatus={toggleRouteSelectedStatus}
                        shareRoute={shareRoute}
                        removeDriver={removeDriver}
                        canResend={canResend}
                        resendToDriver={resendToDriver}
                        toggleRoute={toggleRoute}
                        routeIsSelected={selectedRoutes.includes(routeId)}
                        warnReopen={warnReopen}
                        hasSelectedRoutes={selectedRoutes.length > 0}
                        searchInputValue={searchInputValue}
                        activeByListCommand={
                          backhaulIndex === listKeyboardCommands.currentIndex
                        }
                        setHaveHovered={setHaveHovered}
                        haveHovered={haveHovered}
                      />
                    );
                  })}
              </Box>
            </div>

            {/* <ShareRoute
        open={showShare}
        handleClose={handleShareClose}
        routeId={shareRouteId}
      /> */}
          </div>
        </Grid>
      </Grid>
    </>
  ); /* : (
    <Box p={3}>
      {new Array(3).fill("").map((v, i) => (
        <Box display="flex" key={i} alignItems="center">
          <Skeleton variant="rect" className={classes.stopPlaceholderRect} />
          <Box width="100%" flexShrink={1} ml={2}>
            <Skeleton className={classes.stopPlaceholderTop} variant="rect" />
            <Skeleton
              className={classes.stopPlaceholderBottom}
              variant="rect"
            />
          </Box>
        </Box>
      ))}
    </Box>
  ); */
}

const mapStateToProps = state => ({
  userData: state.auth.userData,
  projectId: state.project.id,
  solution: state.project.solution,
  selectedRoutes: state.common.selectedRoutes,
  selectedVehicles: state.project.selectedVehicles,
  emptyRoutesVisible: state.common.emptyRoutesVisible,
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  isPastProject: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  driverDragging: state.common.draggingType === "drivers",
  expandedFromMap: state.common.expandedFromMap,
  expandedRoutes: state.common.expandedRoutes,
  isRoutesSearchMode: state.common.isRoutesSearchMode
  // projectLoadingInitial: state.loading.projectInitial
});

export default connect(mapStateToProps, {
  setSolution,
  setLoadingRoutesList,
  handleRouteLock,
  setCurrentDriver,
  toggleRouteExpandedStatus,
  toggleRouteSelectedStatus,
  toggleRoute,
  scrollToStop,
  setRoutesSearchMode,
  setProjectTabValue,
  setShowingRouteId
})(withSnackbar(RouteGroups));
